import React, { useCallback, useEffect, useRef, useState } from "react";
import { Prompt } from "react-router";
import cuid from "cuid";
import ReactModal from "react-modal";
import { Location } from "history";
import { Card, Flex } from "rebass";
import set from "lodash/set";
import unset from "lodash/unset";
import merge from "lodash/merge";

import { MODAL_STYLE_SETTINGS } from "../../components/Modal/settings";
import { ControlButton } from "./ControlButton";

interface Props {
  when: boolean,
  matchPath?(location: Location): boolean,
}

interface Callback {
  (res: boolean): void
}

const MODAL_STYLE = merge(
  {},
  MODAL_STYLE_SETTINGS,
  {
    content: {
      maxWidth: "25rem",
    },
  },
);

const RoutePrompt: React.FC<Props> = ({ children, when, matchPath }) => {
  const triggerRef = useRef(Symbol.for(`__PreventTransitionPrompt_${cuid()}`));
  const transitionCallbackRef = useRef<(answer: boolean) => void>(() => {});
  const [open, setOpen] = useState(false);

  /* show Modal */
  const showModal = useCallback((cb: Callback) => {
    transitionCallbackRef.current = cb;
    setOpen(true);
  }, []);
  /* hide Modal */
  const hideModal = useCallback(() => {
    setOpen(false);
  }, []);
  /* Used as message prop in Prompt */
  const handleTransition = useCallback((location: Location) => {
    if (matchPath && matchPath(location)) {
      return true;
    }

    return Symbol.keyFor(triggerRef.current) || false;
  }, [matchPath]);
  /* confirm callback */
  const confirm = useCallback(() => {
    transitionCallbackRef.current(true);
    hideModal();
  }, [hideModal]);
  /* reject callback */
  const reject = useCallback(() => {
    transitionCallbackRef.current(false);
    hideModal();
  }, [hideModal]);
  /* set trigger to window if mount. unset trigger from window if unmount */
  useEffect(() => {
    const { current: trigger } = triggerRef;
    set(window, trigger, showModal);

    return () => {
      unset(window, trigger);
    };
  }, [showModal]);

  return (
    <>
      <Prompt when={when} message={handleTransition} />

      <ReactModal
        isOpen={open}
        onRequestClose={hideModal}
        contentLabel="route-prompt"
        ariaHideApp={false}
        style={MODAL_STYLE}
      >
        <Flex
          width={1}
          height="100%"
          flexDirection="column"
          css="overflow: hidden; border-radius: 5px;"
        >
          <Flex
            flex="1"
            flexDirection="column"
            p="28px 55px 22px"
            bg="sp-gray2"
          >
            {children}
          </Flex>

          <Card
            p="18px"
            bg="#fff"
            borderTop="2px solid"
            borderTopColor="sp-gray"
            css="display: flex; justify-content: space-between;"
          >
            <ControlButton
              border="1px solid"
              borderColor="hoki"
              color="hoki"
              bg="transparent"
              onClick={confirm}
            >
              leave
            </ControlButton>

            <ControlButton
              color="#c9cbdd"
              bg="wild-blue"
              onClick={reject}
            >
              stay
            </ControlButton>
          </Card>
        </Flex>
      </ReactModal>
    </>
  );
};

export default RoutePrompt;
